import React, { useState, useEffect } from "react";
import axios from "axios";
import Navbar from "./OfficerNavbar";
import "./DataTable.css";
import StudentEditForm from "./StudentEditForm";
import StudentPreviewModal from "./StudentPreviewModal";
import { generatePrintHTML } from "./PrintPreviewComponent";
import { baseurl } from "../../url";

const StudentList = () => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [lastAdmissionNumbers, setLastAdmissionNumbers] = useState({});
  const [editableNumbers, setEditableNumbers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentId, setStudentId] = useState("");
  const [previewStudent, setPreviewStudent] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCourseFilter, setSelectedCourseFilter] = useState("");
  const [allApprovedStudents, setAllApprovedStudents] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // NEW: Track processing state for individual students
  const [processingStudentId, setProcessingStudentId] = useState(null);
  const [approvalSuccess, setApprovalSuccess] = useState(null);

  // Function to fetch pending students
  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${baseurl}/api/studentAdmission`);
      const sortedStudents = response.data.sort(
        (a, b) => new Date(b.submissionDate) - new Date(a.submissionDate)
      );
      setStudents(sortedStudents);
      setFilteredStudents(sortedStudents);
      return sortedStudents;
    } catch (error) {
      console.error("Error fetching students:", error);
      setError("Failed to fetch students");
      return [];
    }
  };

  // Function to fetch all approved students
  const fetchAllApprovedStudents = async () => {
    try {
      const response = await axios.get(`${baseurl}/api/approvedStudents`);
      if (response.data && Array.isArray(response.data)) {
        setAllApprovedStudents(response.data);
        return response.data;
      }
      return [];
    } catch (error) {
      console.error("Error fetching approved students:", error);
      return [];
    }
  };

  // Function to calculate admission numbers
  const calculateAdmissionNumbers = (studentsList, approvedList) => {
    const currentYear = new Date().getFullYear().toString().slice(-2);
    const courses = ["B.Tech CSE", "B.Tech ECE", "BBA", "BCA", "MCA"];

    const parseAdmissionNumber = (admissionNumber) => {
      if (!admissionNumber || admissionNumber === "00/00" || admissionNumber === "") {
        return { number: 0, year: currentYear };
      }
      
      if (!admissionNumber.includes("/")) {
        return { number: parseInt(admissionNumber, 10) || 0, year: currentYear };
      }
      
      const parts = admissionNumber.split("/");
      if (parts.length !== 2) {
        return { number: 0, year: currentYear };
      }
      
      const num = parseInt(parts[0], 10);
      return {
        number: isNaN(num) ? 0 : num,
        year: parts[1] || currentYear,
      };
    };

    const formatAdmissionNumber = (number, year) => {
      if (number >= 100) {
        return `${number}/${year}`;
      }
      return `${number.toString().padStart(2, "0")}/${year}`;
    };

    const getAllAdmissionNumbers = (course) => {
      const numbers = [];
      
      // From pending students
      const pendingStudents = studentsList.filter(s => 
        s.course === course && 
        s.admissionNumber && 
        s.admissionNumber !== "" &&
        s.admissionNumber !== "00/00"
      );
      
      for (const student of pendingStudents) {
        const parsed = parseAdmissionNumber(student.admissionNumber);
        if (parsed.year === currentYear) {
          numbers.push(parsed.number);
        }
      }
      
      // From approved students
      if (approvedList && approvedList.length > 0) {
        const approvedStudents = approvedList.filter(s => 
          s.course === course && 
          s.admissionNumber && 
          s.admissionNumber !== "" &&
          s.admissionNumber !== "00/00"
        );
        
        for (const student of approvedStudents) {
          const parsed = parseAdmissionNumber(student.admissionNumber);
          if (parsed.year === currentYear) {
            numbers.push(parsed.number);
          }
        }
      }
      
      return numbers;
    };

    const getMaxNumber = (course) => {
      const numbers = getAllAdmissionNumbers(course);
      if (numbers.length === 0) {
        return 0;
      }
      return Math.max(...numbers);
    };

    const updatedNumbers = {};

    // Handle B.Tech courses together
    const btechCourses = ["B.Tech CSE", "B.Tech ECE"];
    let allBtechNumbers = [];

    for (const course of btechCourses) {
      const numbers = getAllAdmissionNumbers(course);
      allBtechNumbers = [...allBtechNumbers, ...numbers];
    }

    let maxBtechNumber = 0;
    if (allBtechNumbers.length > 0) {
      maxBtechNumber = Math.max(...allBtechNumbers);
    }

    // Set B.Tech numbers
    let cseNumber, eceNumber;
    if (maxBtechNumber === 0) {
      let maxPrevNumber = 0;
      
      const allSources = [...studentsList, ...(approvedList || [])];
      for (const source of allSources) {
        if (btechCourses.includes(source.course) && source.admissionNumber) {
          const parsed = parseAdmissionNumber(source.admissionNumber);
          if (parsed.year !== currentYear && parsed.number > maxPrevNumber) {
            maxPrevNumber = parsed.number;
          }
        }
      }
      
      if (maxPrevNumber > 0) {
        cseNumber = maxPrevNumber + 1;
        eceNumber = maxPrevNumber + 2;
      } else {
        cseNumber = 3433;
        eceNumber = 3434;
      }
    } else {
      cseNumber = maxBtechNumber + 1;
      eceNumber = maxBtechNumber + 2;
    }

    updatedNumbers["B.Tech CSE"] = formatAdmissionNumber(cseNumber, currentYear);
    updatedNumbers["B.Tech ECE"] = formatAdmissionNumber(eceNumber, currentYear);

    // Handle individual courses
    const individualCourses = ["MCA", "BBA", "BCA"];

    for (const course of individualCourses) {
      const maxNumber = getMaxNumber(course);
      
      let nextNumber;
      if (maxNumber === 0) {
        let maxPrevNumber = 0;
        
        const allSources = [...studentsList, ...(approvedList || [])];
        for (const source of allSources) {
          if (source.course === course && source.admissionNumber) {
            const parsed = parseAdmissionNumber(source.admissionNumber);
            if (parsed.year !== currentYear && parsed.number > maxPrevNumber) {
              maxPrevNumber = parsed.number;
            }
          }
        }
        
        if (maxPrevNumber > 0) {
          nextNumber = maxPrevNumber + 1;
        } else {
          if (course === "MCA") {
            nextNumber = 22;
          } else {
            nextNumber = 1;
          }
        }
      } else {
        nextNumber = maxNumber + 1;
      }

      updatedNumbers[course] = formatAdmissionNumber(nextNumber, currentYear);
    }

    return updatedNumbers;
  };

  // Initial data fetch
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const pending = await fetchStudents();
      const approved = await fetchAllApprovedStudents();
      
      if (pending.length > 0 || (approved && approved.length > 0)) {
        const calculatedNumbers = calculateAdmissionNumbers(pending, approved);
        setLastAdmissionNumbers(calculatedNumbers);
        setEditableNumbers(calculatedNumbers);
      }
      setLoading(false);
    };

    loadData();
  }, []);

  // Filter students when search term or course filter changes
  useEffect(() => {
    let filtered = [...students];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (student) =>
          student.name?.toLowerCase().includes(term) ||
          student.course?.toLowerCase().includes(term)
      );
    }

    if (selectedCourseFilter) {
      filtered = filtered.filter(
        (student) => student.course === selectedCourseFilter
      );
    }

    setFilteredStudents(filtered);
  }, [searchTerm, selectedCourseFilter, students]);

  // Function to refresh all data after approval
  const refreshData = async () => {
    setIsProcessing(true);
    try {
      // Fetch both pending and approved students
      const [pending, approved] = await Promise.all([
        fetchStudents(),
        fetchAllApprovedStudents()
      ]);
      
      // Recalculate admission numbers
      const calculatedNumbers = calculateAdmissionNumbers(pending, approved);
      setLastAdmissionNumbers(calculatedNumbers);
      setEditableNumbers(calculatedNumbers);
      
      console.log("Data refreshed successfully");
    } catch (error) {
      console.error("Error refreshing data:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  // UPDATED: handleApprove with individual processing state
  const handleApprove = async (studentId, course) => {
    const confirm = window.confirm(
      "Are you sure you want to Approve this student?"
    );
    if (!confirm) return;

    // Set processing state for this specific student
    setProcessingStudentId(studentId);
    setApprovalSuccess(null);

    try {
      const admissionNumber = editableNumbers[course] || lastAdmissionNumbers[course];
      
      console.log(`Approving ${course} student with: ${admissionNumber}`);
      
      await axios.post(`${baseurl}/api/approve/${studentId}`, {
        admissionNumber,
      });
      
      // Refresh data after approval
      await refreshData();
      
      // Set success state
      setApprovalSuccess(studentId);
      
      // Show success message
      alert("Student Approved successfully.");
      
      // Reset success state after a delay (optional)
      setTimeout(() => {
        setApprovalSuccess(null);
      }, 3000);
      
    } catch (error) {
      console.error("Error approving student:", error);
      alert("Error approving student.");
      setApprovalSuccess(null);
    } finally {
      // Clear processing state
      setProcessingStudentId(null);
    }
  };

  const handleDecline = async (studentId) => {
    const confirm = window.confirm(
      "Are you sure you want to decline this student?"
    );
    if (!confirm) return;

    // Set processing state for decline
    setProcessingStudentId(studentId);

    try {
      await axios.post(`${baseurl}/api/decline/${studentId}`);
      
      // Refresh data after decline
      await refreshData();
      
      alert("Student declined successfully.");
    } catch (error) {
      console.error("Error declining student:", error);
      alert("Error declining student.");
    } finally {
      setProcessingStudentId(null);
    }
  };

  const handlePrintPreview = async (_id) => {
    try {
      const response = await axios.get(`${baseurl}/api/studentDetails/${_id}`);
      const studentDetails = response.data.studentDetails;
      const feeDetails = response.data.studentDetails.feeDetails;

      if (!studentDetails || !studentDetails.parentDetails) {
        console.error("Error: Invalid student details received");
        return;
      }

      const htmlString = generatePrintHTML(
        studentDetails,
        feeDetails,
        studentDetails.photoUrl
      );
      const printWindow = window.open("", "_blank");
      printWindow.document.write(htmlString);
      printWindow.document.close();
    } catch (error) {
      console.error("Error generating print preview:", error);
    }
  };

  const handleEdit = (student) => {
    setSelectedStudent(student);
    setStudentId(student._id);
    setEditMode(true);
  };

  const handleSaveComplete = async () => {
    setEditMode(false);
    setSelectedStudent(null);
    await refreshData();
  };

  const handlePreview = (student) => {
    setPreviewStudent(student);
    setShowPreview(true);
  };

  const handlePreviewEdit = () => {
    setShowPreview(false);
    handleEdit(previewStudent);
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setSelectedCourseFilter("");
  };

  const getCourseBadgeClass = (course) => {
    if (course?.includes("CSE")) return "course-badge cse";
    if (course?.includes("ECE")) return "course-badge ece";
    if (course?.includes("BBA")) return "course-badge bba";
    if (course?.includes("MCA")) return "course-badge mca";
    if (course?.includes("BCA")) return "course-badge bca";
    return "course-badge default";
  };

  const getCourseShortName = (course) => {
    if (course?.includes("CSE")) return "CSE";
    if (course?.includes("ECE")) return "ECE";
    if (course?.includes("BBA")) return "BBA";
    if (course?.includes("MCA")) return "MCA";
    if (course?.includes("BCA")) return "BCA";
    return course || "N/A";
  };

  const totalPending = students.length;
  const totalFiltered = filteredStudents.length;
  const courseCounts = students.reduce((acc, student) => {
    acc[student.course] = (acc[student.course] || 0) + 1;
    return acc;
  }, {});

  const hasActiveFilters = searchTerm || selectedCourseFilter;

  if (loading)
    return (
      <div>
        <Navbar />
        <div className="data-table-container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading student applications...</p>
          </div>
        </div>
      </div>
    );

  if (error)
    return (
      <div>
        <Navbar />
        <div className="data-table-container">
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );

  return (
    <div>
      <Navbar />
      <div className="data-table-container">
        {!editMode ? (
          <div>
            <div className="data-table-header">
              <h1>New Admission Student List</h1>
              <span className="header-badge">
                {totalPending} pending {totalPending === 1 ? "application" : "applications"}
              </span>
              {isProcessing && (
                <span className="processing-badge">⏳ Updating...</span>
              )}
            </div>

            <div className="data-stats">
              <div className="stat-card">
                <span className="stat-icon">📋</span>
                <div>
                  <h4>Total</h4>
                  <p>{totalPending}</p>
                </div>
              </div>
              {Object.entries(courseCounts).map(([course, count]) => (
                <div className="stat-card" key={course}>
                  <span className="stat-icon">🎓</span>
                  <div>
                    <h4>{getCourseShortName(course)}</h4>
                    <p>{count}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="search-toolbar">
              <div className="search-group">
                <input
                  type="text"
                  className="search-input"
                  placeholder="🔍 Search by name or course..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label className="filter-label">Course:</label>
                <select
                  className="filter-dropdown"
                  value={selectedCourseFilter}
                  onChange={(e) => setSelectedCourseFilter(e.target.value)}
                >
                  <option value="">All Courses</option>
                  <option value="B.Tech CSE">BTech CSE</option>
                  <option value="B.Tech ECE">BTech ECE</option>
                  <option value="BBA">BBA</option>
                  <option value="MCA">MCA</option>
                  <option value="BCA">BCA</option>
                </select>
              </div>
              {hasActiveFilters && (
                <button className="clear-filters-btn" onClick={clearAllFilters}>
                  ✕ Clear
                </button>
              )}
              <button 
                className="refresh-btn" 
                onClick={refreshData}
                disabled={isProcessing}
              >
                🔄 Refresh
              </button>
            </div>

            {hasActiveFilters && (
              <div className="filter-info">
                Showing {totalFiltered} of {totalPending} applications
              </div>
            )}

            {filteredStudents.length > 0 ? (
              <div className="data-table-wrapper">
                <table className="students-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Student Name</th>
                      <th>Course</th>
                      <th>Submission Date</th>
                      <th>Admission No</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map((student, index) => {
                      // Check if this specific student is being processed
                      const isThisStudentProcessing = processingStudentId === student._id;
                      const isThisStudentSuccess = approvalSuccess === student._id;

                      return (
                        <tr key={student._id}>
                          <td>{index + 1}</td>
                          <td>
                            <strong>{student.name}</strong>
                            {isThisStudentSuccess && (
                              <span className="success-badge"> ✓ Approved</span>
                            )}
                          </td>
                          <td>
                            <span className={getCourseBadgeClass(student.course)}>
                              {student.course}
                            </span>
                          </td>
                          <td className="submission-date">
                            {student.submissionDate
                              ? new Date(student.submissionDate).toLocaleDateString(
                                  "en-US",
                                  {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  }
                                )
                              : "Not Provided"}
                          </td>
                          <td>
                            <span className="admission-number">
                              {editableNumbers[student.course] ||
                                lastAdmissionNumbers[student.course] ||
                                "—"}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="print-preview-btn"
                                onClick={() => handlePreview(student)}
                                title="Preview Details"
                                disabled={isThisStudentProcessing || isProcessing}
                              >
                                👁️ Preview
                              </button>
                              <button
                                className="print-preview-btn"
                                onClick={() => handlePrintPreview(student._id)}
                                title="Print Preview"
                                disabled={isThisStudentProcessing || isProcessing}
                              >
                                🖨️ Print
                              </button>
                              <button
                                className="Edit-btn"
                                onClick={() => handleEdit(student)}
                                title="Edit Student"
                                disabled={isThisStudentProcessing || isProcessing}
                              >
                                ✏️ Edit
                              </button>
                              <button
                                className={`approve-btn ${isThisStudentProcessing ? 'processing' : ''} ${isThisStudentSuccess ? 'success' : ''}`}
                                onClick={() =>
                                  handleApprove(student._id, student.course)
                                }
                                title="Approve Application"
                                disabled={isThisStudentProcessing || isProcessing || isThisStudentSuccess}
                              >
                                {isThisStudentProcessing ? (
                                  <span className="spinner-small">⏳</span>
                                ) : isThisStudentSuccess ? (
                                  '✓ Approved'
                                ) : (
                                  '✓ Approve'
                                )}
                              </button>
                              <button
                                className="decline-btn"
                                onClick={() => handleDecline(student._id)}
                                title="Decline Application"
                                disabled={isThisStudentProcessing || isProcessing}
                              >
                                ✗ Decline
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="no-admissions-message">
                <span className="emoji">
                  {hasActiveFilters ? "🔍" : "📚"}
                </span>
                <h2>
                  {hasActiveFilters
                    ? "No Matching Applications"
                    : "No New Admissions"}
                </h2>
                <p className="description">
                  {hasActiveFilters
                    ? "Try adjusting your search or filter criteria"
                    : "Currently, there are no new admissions to display. Please check back later."}
                </p>
              </div>
            )}
          </div>
        ) : (
          <StudentEditForm
            student={selectedStudent}
            studentId={studentId}
            onSave={handleSaveComplete}
            onCancel={() => {
              setEditMode(false);
              setSelectedStudent(null);
            }}
            isOpen={editMode}
            showAdmissionNumber={false}
            useDataTableEndpoint={true}
          />
        )}
        {showPreview && (
          <StudentPreviewModal
            student={previewStudent}
            onClose={() => {
              setShowPreview(false);
              setPreviewStudent(null);
            }}
            onEdit={handlePreviewEdit}
          />
        )}
      </div>
    </div>
  );
};

export default StudentList;